import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Grid, CircularProgress, Alert, Table, TableHead, TableRow, TableCell, TableBody, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import SectionCard from '../../components/SectionCard';
import InvButton from '../../components/InvButton';
import StatusChip from '../../components/StatusChip';
import ConfirmDialog from '../../components/ConfirmDialog';
import {
  getGoodsDiscard,
  approveGoodsDiscard,
  rejectGoodsDiscard,
  cancelGoodsDiscard,
} from '../../api/goodsDiscard';
import { getStores } from '../../api/stockLedger';
import { toInventoryList } from '../../api/inventory-request';
import { storeDisplayName } from '../../utils/storeEntity';
import { formatDate, formatDateTime } from '../../utils';
import useInvRole from '../../hooks/useInvRole';
import { INV_ACTIONS } from '../../utils/inventoryRoles';
import { IPD_COLORS } from '../../theme';

/** ConfirmDialog action keys on this screen → the permission each one needs. */
const CONFIRM_ACTION_PERMISSION = {
  approve: INV_ACTIONS.DISCARD_APPROVE,
  reject: INV_ACTIONS.DISCARD_REJECT,
  cancel: INV_ACTIONS.DISCARD_CANCEL,
};

function DetailField({ label, children }) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" sx={{ color: IPD_COLORS.textSecondary, display: 'block' }}>
        {label}
      </Typography>
      <Typography sx={{ color: IPD_COLORS.textPrimary, fontSize: 14 }}>{children || '—'}</Typography>
    </Box>
  );
}

export default function GoodsDiscardDetail() {
  const { id } = useParams();
  const history = useHistory();
  const { can } = useInvRole();
  const { url } = useRouteMatch();
  const listUrl = url.replace(/\/[^/]+$/, '');

  const [record, setRecord] = useState(null);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [acting, setActing] = useState(false);

  useEffect(() => {
    getStores()
      .then(d => setStores(toInventoryList(d)))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // API: GET /inventory/goods-discard/{id}/
      const data = await getGoodsDiscard(id);
      setRecord(data);
    } catch (e) {
      setError(e.message);
      setRecord(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  function openConfirm(action) {
    setConfirmAction(action);
    if (action === 'reject') setRejectReason('');
  }

  function closeConfirm() {
    setConfirmAction(null);
    setRejectReason('');
  }

  async function runConfirm() {
    const required = CONFIRM_ACTION_PERMISSION[confirmAction];
    if (required && !can(required)) return;
    setActing(true);
    try {
      if (confirmAction === 'approve') {
        // API: POST /inventory/goods-discard/{id}/approve/
        await approveGoodsDiscard(id);
        toast.success('Goods discard approved — inventory reduced');
      } else if (confirmAction === 'reject') {
        // API: POST /inventory/goods-discard/{id}/reject/
        await rejectGoodsDiscard(id, { rejection_reason: rejectReason.trim() });
        toast.success('Goods discard rejected');
      } else {
        // API: DELETE /inventory/goods-discard/{id}/
        await cancelGoodsDiscard(id);
        toast.success('Goods discard cancelled');
      }
      closeConfirm();
      load();
    } catch (e) {
      toast.error(e.message || 'Action failed');
    } finally {
      setActing(false);
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress sx={{ color: IPD_COLORS.primary }} />
      </Box>
    );
  }

  if (error || !record) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error || 'Not found'}</Alert>
        <InvButton variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => history.push(listUrl)}>
          Back
        </InvButton>
      </Box>
    );
  }

  const isDraft = record.status === 'DRAFT';
  const store = stores.find(s => (s.store_id || s.id) === record.store_id);
  const lines = Array.isArray(record.lines) ? record.lines : [];

  const confirmTitle = confirmAction === 'approve'
    ? 'Approve Goods Discard'
    : confirmAction === 'reject'
      ? 'Reject Goods Discard'
      : 'Cancel Goods Discard';

  const confirmDescription = confirmAction === 'approve'
    ? `Approve "${record.discard_number}"? Stock will be permanently reduced.`
    : confirmAction === 'reject'
      ? `Reject "${record.discard_number}"? No stock change will occur.`
      : `Cancel "${record.discard_number}"?`;

  return (
    <Box>
      <PageHeader
        eyebrow="Inventory Management · Goods Discard"
        title={record.discard_number || 'Goods Discard'}
        description={record.remarks || 'Goods discard detail'}
        actions={(
          <>
            <InvButton variant="outlined" size="small" startIcon={<ArrowBackIcon />} onClick={() => history.push(listUrl)}>
              Back
            </InvButton>
            {isDraft && (
              <>
                {can(INV_ACTIONS.DISCARD_EDIT) && (
                  <InvButton
                    size="small"
                    variant="outlined"
                    tone="teal"
                    startIcon={<EditIcon />}
                    onClick={() => history.push(`${listUrl}/${id}/edit`)}
                  >
                    Edit
                  </InvButton>
                )}
                {can(INV_ACTIONS.DISCARD_CANCEL) && (
                  <InvButton
                    size="small"
                    variant="outlined"
                    tone="red"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={() => openConfirm('cancel')}
                  >
                    Cancel
                  </InvButton>
                )}
                {can(INV_ACTIONS.DISCARD_REJECT) && (
                  <InvButton
                    size="small"
                    variant="outlined"
                    tone="amber"
                    startIcon={<CloseIcon />}
                    onClick={() => openConfirm('reject')}
                  >
                    Reject
                  </InvButton>
                )}
                {can(INV_ACTIONS.DISCARD_APPROVE) && (
                  <InvButton
                    size="small"
                    startIcon={<CheckIcon />}
                    onClick={() => openConfirm('approve')}
                  >
                    Approve
                  </InvButton>
                )}
              </>
            )}
          </>
        )}
      />

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={4}>
          <SectionCard title="Header" actions={<StatusChip status={record.status} />}>
            <DetailField label="Store">{store ? storeDisplayName(store) : record.store_id}</DetailField>
            <DetailField label="Discard date">{formatDate(record.discard_date)}</DetailField>
            <DetailField label="Remarks">{record.remarks}</DetailField>
            <DetailField label="Created">{formatDateTime(record.created_at)}</DetailField>
            <DetailField label="Approved at">{formatDateTime(record.approved_at)}</DetailField>
            <DetailField label="Approved by">{record.approved_by}</DetailField>
            {record.rejection_reason && (
              <DetailField label="Rejection reason">{record.rejection_reason}</DetailField>
            )}
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={8}>
          <SectionCard title="Lines">
            <Table size="small" sx={{ '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Item</TableCell>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Batch</TableCell>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Reason</TableCell>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary }} align="right">Current</TableCell>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary }} align="right">Discard qty</TableCell>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Expiry</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography sx={{ color: IPD_COLORS.textSecondary }}>No lines</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {lines.map((line) => (
                  <TableRow key={line.id || `${line.item_id}-${line.batch_number}`}>
                    <TableCell>{line.item_name || line.item_id || '—'}</TableCell>
                    <TableCell>{line.batch_number || '—'}</TableCell>
                    <TableCell>{line.reason || '—'}</TableCell>
                    <TableCell align="right">{line.current_quantity ?? '—'}</TableCell>
                    <TableCell align="right">
                      <Typography sx={{ fontWeight: 600, color: IPD_COLORS.warning.text }}>
                        {line.discard_quantity ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDate(line.expiry_date)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </SectionCard>
        </Grid>
      </Grid>

      <ConfirmDialog
        open={Boolean(confirmAction)}
        title={confirmTitle}
        description={confirmDescription}
        destructive={confirmAction === 'cancel'}
        requireReason={confirmAction === 'reject'}
        reasonLabel="Rejection reason"
        reasonValue={rejectReason}
        onReasonChange={setRejectReason}
        loading={acting}
        onConfirm={runConfirm}
        onCancel={closeConfirm}
        confirmLabel={
          confirmAction === 'approve' ? 'Approve'
            : confirmAction === 'reject' ? 'Reject'
              : 'Cancel discard'
        }
      />
    </Box>
  );
}
